import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfile, useUpdateProfile, useDeactivateAccount } from '../hooks/useProfile';
import { Button, Input, Skeleton, Modal } from '../../../shared/components/ui';
import Header from '../../../shared/components/Header';
import Footer from '../../../shared/components/Footer';

const BRAND       = '#082189';
const BRAND_LIGHT = '#f0f3ff';

/* ─────────────────────────────────────────────
   Keyframes
───────────────────────────────────────────── */
const ppFadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0);    }
`;

/* ─────────────────────────────────────────────
   Styled components (unchanged visual language)
───────────────────────────────────────────── */
const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07), 0 1px 4px rgba(0, 0, 0, 0.04);
  animation: ${ppFadeUp} 0.3s ease both;
`;

const BtnPrimary = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 11px 24px;
  background: linear-gradient(135deg, ${BRAND} 0%, #1a3aaa 100%);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.12s;
  font-family: inherit;
  letter-spacing: -0.2px;
  box-shadow: 0 3px 12px rgba(8, 33, 137, 0.28);

  &:hover {
    opacity: 0.9;
    transform: scale(1.01);
  }
`;

const EditToggle = styled.button`
  background: none;
  border: 2px solid rgba(255, 255, 255, 0.4);
  color: #fff;
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  font-family: inherit;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const StatCard = styled.div`
  flex: 1;
  background: #fff;
  border: 2px solid #f3f4f6;
  border-radius: 14px;
  padding: 20px 16px;
  text-align: center;
  transition: border-color 0.15s, transform 0.15s;
  cursor: default;

  &:hover {
    border-color: #c7d2fe;
    transform: translateY(-2px);
  }
`;

const StatsRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const HeroContent = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;

  @media (max-width: 640px) {
    flex-direction: column;
    text-align: center;
  }
`;

const HeroActions = styled.div`
  @media (max-width: 640px) {
    justify-content: center;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const PageMain = styled.main`
  background: #f8fafc;
  min-height: 100vh;
  padding-bottom: 60px;
`;

const PageWrap = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 0 16px;
`;

const Hero = styled.div`
  background: linear-gradient(135deg, ${BRAND} 0%, #1a3aaa 50%, #0d47a1 100%);
  border-radius: 0 0 24px 24px;
  padding: 40px 36px 36px;
  margin-bottom: 28px;
  position: relative;
  overflow: hidden;

  @media (max-width: 480px) {
    padding: 28px 20px 26px;
  }
`;

const HeroBlobTop = styled.div`
  position: absolute;
  top: -60px;
  right: -60px;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
`;

const HeroBlobBottom = styled.div`
  position: absolute;
  bottom: -40px;
  left: -40px;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.04);
`;

const HeroContentRel = styled(HeroContent)`
  position: relative;
`;

const HeroName = styled.h1`
  font-size: 24px;
  font-weight: 800;
  color: #fff;
  margin: 0 0 4px;
  letter-spacing: -0.5px;
`;

const HeroMobile = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.75);
  margin: 0 0 2px;
`;

const HeroEmail = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
`;

const HeroSkelWrap = styled.div`
  background: linear-gradient(135deg, ${BRAND} 0%, #1a3aaa 50%, #0d47a1 100%);
  border-radius: 0 0 24px 24px;
  padding: 40px 36px 36px;
  margin-bottom: 28px;
`;

const AvatarEl = styled.div`
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${BRAND} 0%, #1a3aaa 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${(p) => p.$size * 0.35}px;
  font-weight: 800;
  color: #fff;
  letter-spacing: -1px;
  flex-shrink: 0;
  box-shadow: 0 4px 16px rgba(8, 33, 137, 0.35);
  border: 3px solid rgba(255, 255, 255, 0.25);
`;

const NotLoggedInWrap = styled.div`
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
`;

const NotLoggedInCard = styled(Card)`
  text-align: center;
  padding: 48px 40px;
  max-width: 400px;
  width: 100%;

  @media (max-width: 480px) {
    padding: 36px 22px;
  }
`;

const NotLoggedInIcon = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: ${BRAND_LIGHT};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`;

const NotLoggedInTitle = styled.h2`
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 10px;
  letter-spacing: -0.4px;
`;

const NotLoggedInText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 28px;
  line-height: 1.6;
`;

const EditCard = styled(Card)`
  padding: 28px;
  margin-bottom: 24px;
  animation: ${ppFadeUp} 0.2s ease;

  @media (max-width: 480px) {
    padding: 20px 16px;
  }
`;

const EditTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 20px;
`;

const EditFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const FieldHint = styled.span`
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
  display: block;
`;

const EditButtonsRow = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 800;
  margin-bottom: 4px;
  color: ${(p) => p.$color};
`;

const StatValueSmall = styled.div`
  font-size: 16px;
  font-weight: 800;
  color: #16a34a;
  margin-bottom: 4px;
  line-height: 1.6;
`;

const StatLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
`;

const StatSub = styled.div`
  font-size: 12px;
  color: #9ca3af;
  margin-top: 2px;
`;

const SectionCard = styled(Card)`
  padding: 28px;
  margin-bottom: 24px;

  @media (max-width: 480px) {
    padding: 20px 16px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 20px;
  letter-spacing: -0.3px;
`;

const BookingsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const BookingsTitle = styled.h3`
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.3px;
`;

const BookingsBadge = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  background: #f3f4f6;
  padding: 4px 10px;
  border-radius: 20px;
`;

const EmptyBookings = styled.div`
  text-align: center;
  padding: 32px 0;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.6;
`;

const EmptyTitle = styled.h4`
  font-size: 16px;
  font-weight: 700;
  color: #374151;
  margin: 0 0 8px;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #9ca3af;
  margin: 0 0 24px;
  line-height: 1.5;
`;

const InfoRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;
`;

const InfoLabel = styled.span`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
`;

const InfoValue = styled.span`
  font-size: 14px;
  color: #111827;
  font-weight: 600;
`;

const DangerTitle = styled.h3`
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 6px;
`;

const DangerText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 20px;
`;

const DangerCard = styled(Card)`
  padding: 28px;

  @media (max-width: 480px) {
    padding: 20px 16px;
  }
`;

const ModalText = styled.p`
  font-size: 14px;
  color: #6b7280;
  line-height: 1.6;
  margin: 0 0 24px;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
  }
`;

/* ─────────────────────────────────────────────
   Helper: initials avatar
───────────────────────────────────────────── */
function Avatar({ name, size = 80 }) {
  const initials = (name || '?')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return <AvatarEl $size={size}>{initials}</AvatarEl>;
}

/* ─────────────────────────────────────────────
   Not-logged-in card
───────────────────────────────────────────── */
function NotLoggedIn({ onLogin }) {
  return (
    <NotLoggedInWrap>
      <NotLoggedInCard>
        <NotLoggedInIcon>
          <svg width={34} height={34} viewBox="0 0 24 24" fill="none" stroke={BRAND} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </NotLoggedInIcon>
        <NotLoggedInTitle>You're not logged in</NotLoggedInTitle>
        <NotLoggedInText>
          Log in to view your profile, track bookings, and manage your account.
        </NotLoggedInText>
        <BtnPrimary onClick={onLogin} style={{ width: '100%' }}>
          Login to Continue
        </BtnPrimary>
      </NotLoggedInCard>
    </NotLoggedInWrap>
  );
}

/* ─────────────────────────────────────────────
   Hero skeleton (while profile is loading)
───────────────────────────────────────────── */
function HeroSkeleton() {
  return (
    <HeroSkelWrap>
      <HeroContent>
        <Skeleton width={80} height={80} radius={40} style={{ background: 'rgba(255,255,255,0.25)' }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Skeleton width={200} height={22} style={{ background: 'rgba(255,255,255,0.25)' }} />
          <Skeleton width={140} height={14} style={{ background: 'rgba(255,255,255,0.2)' }} />
          <Skeleton width={180} height={13} style={{ background: 'rgba(255,255,255,0.15)' }} />
        </div>
      </HeroContent>
    </HeroSkelWrap>
  );
}

/* ─────────────────────────────────────────────
   ProfilePage — driven by useProfile()
───────────────────────────────────────────── */
const ProfilePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, openLogin, logout } = useAuth();

  const { data: profile, isPending } = useProfile();
  const updateProfile = useUpdateProfile();
  const deactivate = useDeactivateAccount();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ name: '', email: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);

  const startEdit = () => {
    setDraft({ name: profile?.name || '', email: profile?.email || '' });
    updateProfile.reset();
    setEditing(true);
  };

  const cancelEdit = () => {
    updateProfile.reset();
    setEditing(false);
  };

  const saveEdit = () => {
    if (!draft.name.trim()) return;
    updateProfile.mutate(
      { name: draft.name.trim(), email: draft.email.trim(), mobile: profile?.mobile || '' },
      { onSuccess: () => setEditing(false) },
    );
  };

  const handleSignOut = () => {
    logout();
    navigate('/');
  };

  const handleDeactivate = () => {
    deactivate.mutate(undefined, {
      onSuccess: () => { setConfirmOpen(false); navigate('/'); },
    });
  };

  const fieldErrors = updateProfile.error?.fieldErrors || {};

  return (
    <>
      <Header />

      <PageMain>
        {!isAuthenticated ? (
          <NotLoggedIn onLogin={openLogin} />
        ) : (
          <PageWrap>

            {/* ── Profile Hero ── */}
            {isPending && !profile ? (
              <HeroSkeleton />
            ) : (
              <Hero>
                <HeroBlobTop />
                <HeroBlobBottom />

                <HeroContentRel>
                  <Avatar name={profile?.name} size={80} />
                  <div style={{ flex: 1 }}>
                    <HeroName>{profile?.name || 'CityCraft User'}</HeroName>
                    <HeroMobile>+91 {profile?.mobile}</HeroMobile>
                    {profile?.email && (
                      <HeroEmail>{profile.email}</HeroEmail>
                    )}
                  </div>
                  {!editing && (
                    <HeroActions>
                      <EditToggle onClick={startEdit} type="button">
                        Edit Profile
                      </EditToggle>
                    </HeroActions>
                  )}
                </HeroContentRel>
              </Hero>
            )}

            {/* ── Edit Form ── */}
            {editing && (
              <EditCard>
                <EditTitle>Edit Profile</EditTitle>
                <EditFields>
                  <Input
                    label="FULL NAME"
                    type="text"
                    value={draft.name}
                    onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                    placeholder="Your full name"
                    error={fieldErrors.name}
                  />
                  <div>
                    <Input
                      label="MOBILE NUMBER"
                      type="tel"
                      value={`+91 ${profile?.mobile || ''}`}
                      disabled
                      style={{ background: '#f9fafb', color: '#6b7280', cursor: 'not-allowed' }}
                    />
                    <FieldHint>Mobile number cannot be changed</FieldHint>
                  </div>
                  <Input
                    label="EMAIL ADDRESS"
                    type="email"
                    value={draft.email}
                    onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
                    placeholder="you@example.com"
                    error={fieldErrors.email}
                  />
                </EditFields>
                <EditButtonsRow>
                  <Button
                    variant="primary"
                    onClick={saveEdit}
                    loading={updateProfile.isPending}
                    disabled={!draft.name.trim()}
                  >
                    Save Changes
                  </Button>
                  <Button variant="ghost" onClick={cancelEdit} disabled={updateProfile.isPending}>
                    Cancel
                  </Button>
                </EditButtonsRow>
              </EditCard>
            )}

            {/* ── Stats Row ── */}
            <StatsRow>
              <StatCard>
                <StatValue $color={BRAND}>0</StatValue>
                <StatLabel>Bookings</StatLabel>
                <StatSub>Total services booked</StatSub>
              </StatCard>
              <StatCard>
                <StatValue $color="#f59e0b">★ 4.8</StatValue>
                <StatLabel>Rating</StatLabel>
                <StatSub>Average satisfaction</StatSub>
              </StatCard>
              <StatCard>
                <StatValueSmall>Valued</StatValueSmall>
                <StatLabel>Customer</StatLabel>
                <StatSub>Membership status</StatSub>
              </StatCard>
            </StatsRow>

            {/* ── Quick Actions ── */}
            <SectionCard>
              <SectionTitle>Quick Actions</SectionTitle>
              <ActionsRow>
                <Button variant="outline" onClick={() => navigate('/profile/addresses')}>
                  Manage Addresses
                </Button>
                <Button variant="primary" onClick={() => navigate('/bookings')}>
                  My Bookings
                </Button>
              </ActionsRow>
            </SectionCard>

            {/* ── Bookings Section ── */}
            <SectionCard>
              <BookingsHeader>
                <BookingsTitle>Your Bookings</BookingsTitle>
                <BookingsBadge>0 bookings</BookingsBadge>
              </BookingsHeader>
              <EmptyBookings>
                <EmptyIcon>📋</EmptyIcon>
                <EmptyTitle>No bookings yet</EmptyTitle>
                <EmptyText>
                  You haven't booked any services yet.<br />
                  Explore our home services and book your first one!
                </EmptyText>
                <BtnPrimary onClick={() => navigate('/')}>
                  Book a Service →
                </BtnPrimary>
              </EmptyBookings>
            </SectionCard>

            {/* ── Account Info ── */}
            <SectionCard>
              <SectionTitle>Account Information</SectionTitle>
              <InfoRows>
                {[
                  { label: 'Name', value: profile?.name || '—' },
                  { label: 'Mobile', value: `+91 ${profile?.mobile || ''}` },
                  { label: 'Email', value: profile?.email || '—' },
                  { label: 'Member Since', value: new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) },
                ].map(({ label, value }) => (
                  <InfoRow key={label}>
                    <InfoLabel>{label}</InfoLabel>
                    <InfoValue>{value}</InfoValue>
                  </InfoRow>
                ))}
              </InfoRows>
            </SectionCard>

            {/* ── Danger Zone ── */}
            <DangerCard>
              <DangerTitle>Account Actions</DangerTitle>
              <DangerText>
                You'll need to log in again after signing out. Deactivating your account is permanent.
              </DangerText>
              <ActionsRow>
                <Button variant="danger" onClick={handleSignOut}>
                  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  Sign Out
                </Button>
                <Button variant="danger" onClick={() => setConfirmOpen(true)}>
                  Deactivate Account
                </Button>
              </ActionsRow>
            </DangerCard>
          </PageWrap>
        )}
      </PageMain>

      <Footer />

      {/* ── Deactivate confirmation ── */}
      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)} title="Deactivate account?" maxWidth={420}>
        <ModalText>
          This will deactivate your CityCraft account and sign you out. This action cannot be undone.
          Are you sure you want to continue?
        </ModalText>
        <ModalActions>
          <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={deactivate.isPending}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeactivate} loading={deactivate.isPending}>
            Deactivate
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
};

export default ProfilePage;
